import {
	getAuth,
	signInWithPopup,
	GoogleAuthProvider,
	FacebookAuthProvider,
	TwitterAuthProvider,
	OAuthProvider,
} from 'firebase/auth';
import { initializeApp } from 'firebase/app';
import toast from 'react-hot-toast';
import { t } from 'i18next';

const firebaseConfig = {
	apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
	authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
	projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
	storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
	messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
	appId: import.meta.env.VITE_FIREBASE_APP_ID,
	measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

export const firebaseApp = initializeApp(firebaseConfig);

// ----------------------------------Google Login--------------------------------------------

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
	prompt: 'select_account',
});
// Explicitly request email scope
googleProvider.addScope('email');
googleProvider.addScope('profile');

export const auth = getAuth();
export const signInWithGooglePopup = () => signInWithPopup(auth, googleProvider);

// ----------------------------------Facebook Login--------------------------------------------

const fbProvider = new FacebookAuthProvider();
fbProvider.setCustomParameters({
	display: 'popup',
});
// Explicitly request email permission
fbProvider.addScope('email');

export const signInWithFacebookPopup = () => signInWithPopup(auth, fbProvider);

// ----------------------- Twitter Login ----------------------------

const twitterProvider = new TwitterAuthProvider();
// Twitter doesn't support email scope, but we can try to get it from user info
export const signInWithXPopup = () => signInWithPopup(auth, twitterProvider);

const appleProvider = new OAuthProvider('apple.com');
appleProvider.addScope('email');
appleProvider.addScope('name');
appleProvider.setCustomParameters({
	locale: 'en',
});

export const signInWithApplePopup = () => signInWithPopup(auth, appleProvider);

// ----------------------- Email Handling Helper ----------------------------

export const extractUserEmail = (user: any): { email: string; isEmailMissing: boolean } => {
	let email = '';
	let isEmailMissing = false;

	// Try multiple sources for email, including private email relay addresses
	if (user?.email) {
		email = user.email;
	} else if (user?._tokenResponse?.email) {
		email = user._tokenResponse.email;
	} else if (user?.user?.email) {
		email = user.user.email;
	} else if (user?.providerData && user.providerData.length > 0) {
		// Check provider data for email (including Apple private relay emails)
		const providerData = user.providerData.find((p: any) => p.email);
		if (providerData?.email) {
			email = providerData.email;
		}
	} else if (user?.user?.providerData && user.user.providerData.length > 0) {
		// Check nested provider data (including Apple private relay emails)
		const providerData = user.user.providerData.find((p: any) => p.email);
		if (providerData?.email) {
			email = providerData.email;
		}
	}

	// Additional checks for Apple Sign In private email relay
	if (!email && user?.user) {
		// Check for Apple private email in additional response fields
		if (user.user.providerData) {
			const appleProvider = user.user.providerData.find((p: any) => p.providerId === 'apple.com');
			if (appleProvider?.email) {
				email = appleProvider.email;
			}
		}

		// Check for private email in additional token response fields
		if (!email && user._tokenResponse) {
			// Apple sometimes provides private email in different fields
			if (user._tokenResponse.privateEmail) {
				email = user._tokenResponse.privateEmail;
			} else if (user._tokenResponse.apple_email) {
				email = user._tokenResponse.apple_email;
			}
		}
	}

	// For Google, check additional fields that might contain private emails
	if (!email && user?.user) {
		if (user.user.providerData) {
			const googleProvider = user.user.providerData.find((p: any) => p.providerId === 'google.com');
			if (googleProvider?.email) {
				email = googleProvider.email;
			}
		}
	}

	// For Facebook, check additional fields
	if (!email && user?.user) {
		if (user.user.providerData) {
			const facebookProvider = user.user.providerData.find(
				(p: any) => p.providerId === 'facebook.com',
			);
			if (facebookProvider?.email) {
				email = facebookProvider.email;
			}
		}
	}

	// Check if email is missing or empty
	if (!email || email.trim() === '') {
		isEmailMissing = true;
	}

	return { email, isEmailMissing };
};

// ----------------------- Error Handling ----------------------------

const handleAuthError = (error: any, platform: string) => {
	let errorMessage = 'An unexpected error occurred while signing in.';
	if (error.code) {
		switch (error.code) {
			case 'auth/invalid-email':
				errorMessage = t('Auth.invalidEmail');
				break;
			case 'auth/email-already-in-use':
				errorMessage = t('Auth.emailInUse');
				break;
			case 'auth/wrong-password':
				errorMessage = t('Auth.wrongPassword');
				break;
			case 'auth/weak-password':
				errorMessage = t('Auth.weakPassword');
				break;
			case 'auth/user-disabled':
				errorMessage = t('Auth.userDisabled');
				break;
			case 'auth/user-not-found':
				errorMessage = t('Auth.userNotFound');
				break;
			case 'auth/account-exists-with-different-credential':
				errorMessage = t('Auth.accountExistsWithDifferentCredential', { platform });
				break;
			case 'auth/operation-not-allowed':
				errorMessage = t('Auth.operationNotAllowed');
				break;
			case 'auth/credential-already-in-use':
				errorMessage = t('Auth.credentialInUse');
				break;
			case 'auth/popup-closed-by-user':
				// Don't show error for user-initiated popup closure
				console.log(`${platform} login popup closed by user`);
				return;
			case 'auth/cancelled-popup-request':
				// Don't show error for user-initiated popup cancellation
				console.log(`${platform} login popup cancelled by user`);
				return;
			case 'auth/popup-blocked':
				errorMessage = t('Auth.popupBlocked');
				break;
			case 'auth/network-request-failed':
				errorMessage = t('Auth.networkError');
				break;
			case 'auth/quota-exceeded':
				errorMessage = t('Auth.quotaExceeded');
				break;
			case 'auth/missing-phone-number':
				errorMessage = t('Auth.missingPhoneNumber');
				break;
			case 'auth/invalid-verification-code':
				errorMessage = t('Auth.invalidVerificationCode');
				break;
			case 'auth/missing-verification-code':
				errorMessage = t('Auth.missingVerificationCode');
				break;
			case 'auth/timeout':
				errorMessage = t('Auth.timeout');
				break;
			case 'auth/too-many-requests':
				errorMessage = t('Auth.tooManyRequests');
				break;
			case 'auth/internal-error':
				errorMessage = t('Auth.internalError');
				break;
			default:
				errorMessage =
					error?.message || t('Auth.defaultError', { platform, message: error.message });
		}
	}

	toast.error(errorMessage);
	console.error(`${platform} Auth Error:`, error);
};

// ----------------------- Social Login with Email Check ----------------------------

export const handleSocialLoginWithEmailCheck = async (
	loginFunction: () => Promise<any>,
	provider: string,
	onSuccess: (userData: any) => void,
	onMissingEmail: (provider: string, userData: any) => void,
) => {
	try {
		const response = await loginFunction();
		const { isEmailMissing } = extractUserEmail(response);

		if (isEmailMissing) {
			// Show appropriate message based on provider
			switch (provider.toLowerCase()) {
				case 'google':
					toast.error(t('Auth.emailPermissionRequired', { provider: 'Google' }));
					break;
				case 'facebook':
					toast.error(t('Auth.emailPermissionRequired', { provider: 'Facebook' }));
					break;
				case 'twitter':
					toast.error(t('Auth.twitterEmailNotAvailable'));
					break;
				case 'apple':
					toast.error(t('Auth.emailPermissionRequired', { provider: 'Apple' }));
					break;
				default:
					toast.error(t('Auth.emailRequired'));
			}
			onMissingEmail(provider, response);
			return;
		}
console.log(`${provider} login successful:`, response);
		onSuccess(response);
	} catch (error: any) {
		// Check if the error is related to popup cancellation
		if (
			error.code === 'auth/popup-closed-by-user' ||
			error.code === 'auth/cancelled-popup-request' ||
			error.code === 'auth/popup-blocked'
		) {
			// Don't show error toast for user-initiated cancellations
			console.log(`${provider} login cancelled by user`);
			return;
		}
console.error(`${provider} Login Error:`, error);
		// Let the original error handler deal with other errors
		handleAuthError(error, provider);
	}
};
